import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Rocket, Briefcase, Wallet, Film, Shield, Wrench, Instagram } from 'lucide-react';
import { CONTACT_EMAIL, INSTAGRAM_HANDLE, INSTAGRAM_URL } from '../constants/contact';
import PageSeo from '../components/seo/PageSeo';
import { getPublicSiteUrl } from '../config/runtimeConfig';
import { buildFaqPageSchema } from '../utils/seoSchemas';

const CentralAjuda = () => {
  const [openCategory, setOpenCategory] = useState(null);
  const [openQuestion, setOpenQuestion] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const iconMap = { Rocket, Briefcase, Wallet, Film, Shield, Wrench };
  const categories = [
    {
      id: 'getting-started',
      title: 'Começando',
      icon: 'Rocket',
      questions: [
        {
          id: 'como-criar-conta',
          question: 'Como criar uma conta?',
          answer: 'Para criar uma conta, clique em "Cadastrar" no canto superior direito da página. Preencha seus dados pessoais, escolha se você é cliente ou videomaker, e confirme seu email. É rápido e gratuito!'
        },
        {
          id: 'como-funciona',
          question: 'Como funciona a plataforma?',
          answer: 'A Ufokko conecta clientes a videomakers em modo sob demanda: o cliente cria o job com local, data e valor; videomakers dentro do raio de busca veem o pedido e podem aceitar. Após o aceite, o pagamento é tratado com segurança (escrow) até a conclusão e aprovação do trabalho.'
        },
        {
          id: 'tipos-usuarios',
          question: 'Posso ser cliente e videomaker ao mesmo tempo?',
          answer: 'Sim! Você pode ter ambas as funcionalidades. Ao criar sua conta, você escolhe seu perfil principal, mas pode alternar entre os dois modos a qualquer momento no seu dashboard.'
        }
      ]
    },
    {
      id: 'jobs',
      title: 'Jobs e matching',
      icon: 'Briefcase',
      questions: [
        {
          id: 'criar-job',
          question: 'Como criar um job?',
          answer: 'Após fazer login como cliente, acesse seu dashboard e clique em "Criar Job". Preencha os detalhes: título, descrição, categoria, localização, data do evento, valor e extras desejados. O job fica visível para videomakers no raio configurado, que podem aceitar diretamente.'
        },
        {
          id: 'aceitar-job-videomaker',
          question: 'Como aceitar um job como videomaker?',
          answer: 'No app, abra o feed de jobs próximos à sua localização. Toque no job para ver os detalhes e use a opção de aceitar quando estiver disponível e dentro do seu raio. Em seguida, acompanhe chat, prazos e entrega pelo próprio app.'
        },
        {
          id: 'pagamento-apos-aceite',
          question: 'Como funciona o pagamento após o aceite?',
          answer: 'Depois que um videomaker aceita o job, o fluxo de pagamento segue as regras do app (cartão via Stripe, com valor retido até a aprovação da entrega). Use as telas de pagamento indicadas no aplicativo para concluir a autorização com segurança.'
        },
        {
          id: 'cancelar-job',
          question: 'Posso cancelar um job?',
          answer: 'Sim, em geral você pode cancelar um job enquanto ainda não houver aceite e pagamento concluídos conforme o estágio do pedido. Após aceite e pagamento, o cancelamento segue nossa política de reembolso.',
          showRefundPolicyLink: true
        }
      ]
    },
    {
      id: 'payments',
      title: 'Pagamentos',
      icon: 'Wallet',
      questions: [
        {
          id: 'como-pagar',
          question: 'Como realizar o pagamento?',
          answer: 'Após o videomaker aceitar o job, siga as instruções no app para autorizar o pagamento com cartão (processado de forma segura pelo Stripe). O valor fica em escrow até a conclusão e aprovação do trabalho.'
        },
        {
          id: 'escrow',
          question: 'O que é escrow?',
          answer: 'Escrow é um sistema de garantia onde o pagamento fica retido de forma segura até a conclusão do trabalho. Isso protege tanto o cliente quanto o videomaker. O pagamento só é liberado após a aprovação do trabalho pelo cliente.'
        },
        {
          id: 'taxas',
          question: 'Quais são as taxas?',
          answer: 'A plataforma cobra uma taxa de serviço sobre cada transação. Esta taxa é claramente informada antes do pagamento. Videomakers recebem o valor acordado menos a taxa da plataforma. Não há taxas de cadastro ou mensalidades.'
        },
        {
          id: 'reembolso',
          question: 'Como solicitar reembolso?',
          answer: 'Se você precisar solicitar um reembolso, entre em contato conosco através do email contato@ufokko.com.br. Analisaremos seu caso de acordo com nossa política de cancelamento e reembolso.',
          showRefundPolicyLink: true
        }
      ]
    },
    {
      id: 'videomakers',
      title: 'Para Videomakers',
      icon: 'Film',
      questions: [
        {
          id: 'cadastro-videomaker',
          question: 'Como me cadastrar como videomaker?',
          answer: 'Ao criar sua conta, escolha a opção "Videomaker". Complete seu perfil com informações profissionais, adicione seu portfólio, defina sua área de atuação e raio de alcance. Quanto mais completo seu perfil, mais jobs você receberá.'
        },
        {
          id: 'portfolio',
          question: 'Como adicionar trabalhos ao portfólio?',
          answer: 'Acesse seu dashboard e vá em "Portfólio". Clique em "Adicionar Trabalho" e faça upload de imagens ou vídeos dos seus trabalhos anteriores. Você também pode adicionar links para seus trabalhos online.'
        },
        {
          id: 'receber-pagamento',
          question: 'Quando recebo o pagamento?',
          answer: 'O pagamento é liberado automaticamente após o cliente aprovar o trabalho entregue. Se o cliente não aprovar ou solicitar alterações, o pagamento permanece em escrow até a resolução. Em caso de disputa, nossa equipe analisa o caso.'
        },
        {
          id: 'raio-atuacao',
          question: 'Como definir meu raio de atuação?',
          answer: 'No seu perfil de videomaker, você pode definir o raio de atuação em quilômetros. Jobs dentro deste raio aparecerão no seu feed. Você pode ajustar este raio a qualquer momento nas configurações do perfil.'
        }
      ]
    },
    {
      id: 'safety',
      title: 'Segurança e Privacidade',
      icon: 'Shield',
      questions: [
        {
          id: 'dados-seguros',
          question: 'Meus dados estão seguros?',
          answer: 'Sim! Utilizamos criptografia de ponta a ponta, armazenamento seguro, e seguimos todas as normas da LGPD (Lei Geral de Proteção de Dados). Suas informações de pagamento são processadas pelo Stripe, uma das plataformas de pagamento mais seguras do mundo.'
        },
        {
          id: 'lgpd',
          question: 'Quais são meus direitos sob a LGPD?',
          answer: 'Você tem direito a acessar, corrigir, excluir e portar seus dados pessoais. Você também pode revogar seu consentimento a qualquer momento. Para exercer estes direitos, entre em contato conosco através do email contato@ufokko.com.br.'
        },
        {
          id: 'privacidade',
          question: 'Minhas informações são compartilhadas?',
          answer: 'Não vendemos suas informações. Compartilhamos dados apenas com prestadores de serviços essenciais (como Stripe para pagamentos) e quando exigido por lei. Consulte nossa Política de Privacidade para mais detalhes.'
        }
      ]
    },
    {
      id: 'technical',
      title: 'Problemas Técnicos',
      icon: 'Wrench',
      questions: [
        {
          id: 'app-nao-funciona',
          question: 'O app não está funcionando. O que fazer?',
          answer: 'Primeiro, tente fechar e reabrir o app. Se o problema persistir, verifique sua conexão com a internet. Se ainda assim não funcionar, desinstale e reinstale o app. Para problemas persistentes, entre em contato conosco.'
        },
        {
          id: 'nao-recebo-notificacoes',
          question: 'Não estou recebendo notificações. Como resolver?',
          answer: 'Verifique as configurações de notificação do seu dispositivo. No app, vá em Configurações e certifique-se de que as notificações estão habilitadas. Se o problema persistir, entre em contato conosco.'
        },
        {
          id: 'problema-pagamento',
          question: 'Tive um problema com o pagamento. O que fazer?',
          answer: 'Se você teve um problema com o pagamento, verifique se há fundos suficientes no cartão. Se o problema persistir, entre em contato conosco através do email contato@ufokko.com.br com os detalhes do problema.'
        }
      ]
    }
  ];

  const filteredCategories = categories.map(category => ({
    ...category,
    questions: category.questions.filter(q => 
      q.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
      q.answer.toLowerCase().includes(searchTerm.toLowerCase())
    )
  })).filter(category => category.questions.length > 0);

  const allFaqItems = categories.flatMap((category) =>
    category.questions.map((q) => ({ q: q.question, a: q.answer }))
  );
  const helpUrl = `${getPublicSiteUrl()}/ajuda`;
  const helpJsonLd = buildFaqPageSchema(allFaqItems, helpUrl);

  return (
    <div className="min-h-full">
      <PageSeo
        title="Central de Ajuda | Ufokko"
        description="Perguntas frequentes sobre jobs, pagamentos, portfólio, Stripe, LGPD e uso do app Ufokko para clientes e videomakers."
        path="/ajuda"
        jsonLd={helpJsonLd}
      />
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-[#0E76FF] to-[#2563EB] text-white py-16">
        <div className="container mx-auto px-6 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-5xl font-bold mb-4"
          >
            Central de Ajuda
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-xl text-white/90 mb-8"
          >
            Encontre respostas para suas dúvidas
          </motion.p>

          {/* Search */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="max-w-2xl mx-auto"
          >
            <div className="relative">
              <input
                type="text"
                placeholder="Buscar ajuda..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-6 py-4 rounded-full text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-white shadow-lg"
              />
              <span className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400">
                🔍
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <main className="container mx-auto px-6 py-12 max-w-4xl">
        {filteredCategories.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-600 dark:text-gray-400">
              Nenhum resultado encontrado para "{searchTerm}". Tente buscar com outras palavras.
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {filteredCategories.map((category) => (
              <motion.div
                key={category.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-hidden"
              >
                <button
                  onClick={() => setOpenCategory(openCategory === category.id ? null : category.id)}
                  className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    {iconMap[category.icon] && (() => {
                      const Icon = iconMap[category.icon];
                      return <Icon className="w-8 h-8 text-primary shrink-0" aria-hidden />;
                    })()}
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                      {category.title}
                    </h2>
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      ({category.questions.length})
                    </span>
                  </div>
                  <span className="text-gray-400">
                    {openCategory === category.id ? '▲' : '▼'}
                  </span>
                </button>

                <AnimatePresence>
                  {openCategory === category.id && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden"
                    >
                      <div className="px-6 py-4 space-y-4 border-t border-gray-200 dark:border-gray-700">
                        {category.questions.map((item) => (
                          <div key={item.id}>
                            <button
                              onClick={() => setOpenQuestion(openQuestion === item.id ? null : item.id)}
                              className="w-full text-left flex items-start justify-between gap-4 p-4 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                            >
                              <span className="font-semibold text-gray-900 dark:text-white flex-1">
                                {item.question}
                              </span>
                              <span className="text-gray-400 flex-shrink-0">
                                {openQuestion === item.id ? '▲' : '▼'}
                              </span>
                            </button>
                            <AnimatePresence>
                              {openQuestion === item.id && (
                                <motion.div
                                  initial={{ height: 0, opacity: 0 }}
                                  animate={{ height: 'auto', opacity: 1 }}
                                  exit={{ height: 0, opacity: 0 }}
                                  className="overflow-hidden"
                                >
                                  <div className="px-4 pb-4 text-gray-600 dark:text-gray-300">
                                    {item.answer}
                                    {item.showRefundPolicyLink && (
                                      <> Consulte a <Link to="/politica-cancelamento-reembolso" className="text-primary dark:text-blue-400 underline hover:no-underline font-medium">Política de Cancelamento e Reembolso</Link>.</>
                                    )}
                                  </div>
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        )}

        {/* Contact Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mt-12 bg-gradient-to-r from-[#0E76FF] to-[#2563EB] rounded-2xl shadow-lg p-8 text-white text-center"
        >
          <h2 className="text-2xl font-bold mb-4">
            Ainda precisa de ajuda?
          </h2>
          <p className="text-white/90 mb-6">
            Nossa equipe está pronta para ajudá-lo
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              className="px-6 py-3 bg-white text-primary rounded-full font-semibold hover:shadow-lg transition-shadow inline-flex items-center justify-center gap-2"
            >
              Enviar Email
            </a>
            <a
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="px-6 py-3 bg-white text-primary rounded-full font-semibold hover:shadow-lg transition-shadow inline-flex items-center justify-center gap-2"
            >
              <Instagram className="w-5 h-5" aria-hidden />
              Seguir @{INSTAGRAM_HANDLE}
            </a>
          </div>
        </motion.div>
      </main>
    </div>
  );
};

export default CentralAjuda;

